import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil } from "lucide-react";
import { api, formatApiError, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LinkSelect, linkPayload } from "@/components/scan/LinkSelect";
import { AssigneeSelect } from "@/components/planner/AssigneeSelect";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const TodoDialog = ({ open, editing, onClose, onSaved }) => {
  const [f, setF] = useState({});
  useEffect(() => { if (open) setF(editing ? { ...editing, link: { customer_id: editing.customer_id, portfolio_id: editing.portfolio_id } } : { title: "", notes: "", due_date: "", assignee_id: null, link: {} }); }, [open, editing]);
  const submit = async (e) => {
    e.preventDefault();
    const body = { title: f.title, notes: f.notes, due_date: f.due_date || null, assignee_id: f.assignee_id, ...linkPayload(f.link || {}) };
    try { editing ? await api.put(`/todos/${editing.id}`, body) : await api.post("/todos", body); toast.success("Kaydedildi"); onSaved(); onClose(); }
    catch (x) { toast.error(formatApiError(x.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="todo-dialog">
        <DialogHeader><DialogTitle>{editing ? "Görevi düzenle" : "Yeni görev"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label className="text-xs">Görev *</Label><Input data-testid="todo-title" required minLength={2} value={f.title || ""} onChange={(e) => setF({ ...f, title: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Son tarih</Label><Input data-testid="todo-due" type="date" value={f.due_date || ""} onChange={(e) => setF({ ...f, due_date: e.target.value })} /></div>
            <AssigneeSelect testid="todo-assignee" value={f.assignee_id} onChange={(v) => setF({ ...f, assignee_id: v })} />
          </div>
          <LinkSelect value={f.link || {}} onChange={(v) => setF({ ...f, link: v })} />
          <div><Label className="text-xs">Not</Label><Textarea rows={2} value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
          <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={onClose}>İptal</Button><Button data-testid="todo-save-btn" type="submit" className="bg-emerald-600 hover:bg-emerald-700">Kaydet</Button></div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function TodosPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("open");
  const [dialog, setDialog] = useState(null);
  const load = () => api.get("/todos").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const act = async (fn) => { try { await fn(); load(); } catch (x) { toast.error(formatApiError(x.response?.data?.detail)); } };
  const today = new Date().toISOString().slice(0, 10);
  const shown = items.filter((t) => (filter === "open" ? !t.done : filter === "done" ? t.done : t.assignee_id === user.id && !t.done));

  return (
    <div className="space-y-6 max-w-4xl" data-testid="todos-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Yapılacaklar</h1>
          <p className="text-slate-500 text-sm mt-1">{user.role === "BROKER" ? "Kendi görevlerinizi tutun ve danışmanlarınıza görev atayın." : "Kişisel görevleriniz ve Broker'ın size atadığı görevler."}</p>
        </div>
        <Button data-testid="new-todo-btn" className="bg-slate-900" onClick={() => setDialog({})}><Plus className="w-4 h-4 mr-1" />Yeni görev</Button>
      </div>
      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList>
          <TabsTrigger value="open" data-testid="todo-tab-open">Açık ({items.filter((t) => !t.done).length})</TabsTrigger>
          <TabsTrigger value="mine" data-testid="todo-tab-mine">Bana atanan</TabsTrigger>
          <TabsTrigger value="done" data-testid="todo-tab-done">Tamamlanan</TabsTrigger>
        </TabsList>
      </Tabs>
      <Card className="divide-y">
        {shown.length === 0 && <div className="p-10 text-center text-sm text-slate-500">Görev yok</div>}
        {shown.map((t) => (
          <div key={t.id} data-testid={`todo-row-${t.id}`} className="p-3 flex items-start gap-3">
            <Checkbox data-testid={`todo-check-${t.id}`} className="mt-1" checked={t.done} onCheckedChange={(v) => act(() => api.put(`/todos/${t.id}/done`, { done: !!v }))} />
            <div className="flex-1 min-w-0">
              <div className={`text-sm font-medium ${t.done ? "line-through text-slate-400" : "text-slate-900"}`}>{t.title}</div>
              <div className="text-xs text-slate-500 flex flex-wrap gap-x-3">
                {t.due_date && <span className={!t.done && t.due_date < today ? "text-red-600 font-semibold" : ""}>Son: {formatDate(t.due_date)}</span>}
                <span>Sorumlu: {t.assignee_name}</span>
                {t.created_by !== t.assignee_id && <span>Atayan: {t.created_by_name}</span>}
              </div>
              {t.notes && <div className="text-xs text-slate-600 mt-0.5">{t.notes}</div>}
            </div>
            <Button size="icon" variant="ghost" data-testid={`todo-edit-${t.id}`} onClick={() => setDialog({ editing: t })}><Pencil className="w-4 h-4" /></Button>
            {(user.role === "BROKER" || t.created_by === user.id) && <Button size="icon" variant="ghost" data-testid={`todo-delete-${t.id}`} onClick={() => act(() => api.delete(`/todos/${t.id}`))}><Trash2 className="w-4 h-4 text-red-600" /></Button>}
          </div>
        ))}
      </Card>
      <TodoDialog open={!!dialog} editing={dialog?.editing} onClose={() => setDialog(null)} onSaved={load} />
    </div>
  );
}
